import { useCallback, useLayoutEffect } from "react"
import { useNavigate } from "react-router-dom"
import PropTypes from "prop-types"
import { Box, Paper, Divider } from "@mui/material"
import { map } from "lodash"
import Header from "components/assets-header"
import { NORMALIZATION } from "helpers/segments"
import { DATE, FILTER_ON_GROUP, SEARCHABLE_MENU_ITEM, PERIOD, FILTER } from "helpers/shapes"
import DateSelector from "components/date-picker"
import AISummarizer from "modules/ai-summary"
import { ACTIONS } from "modules/ai-summary/constants"
import AnalyzeFactorsRisk from "modules/analyze-factors-risk"
import AnalyzeFactorsPerformance from "modules/analyze-factors-performance"
import SearchableMenu from "components/searchable-menu"
import PeriodSelector from "components/period-selector"
import { useCheckLastPreferenceRedirect, useSaveContextPreferences } from "hooks/preferences"

export default function AnalyzeFactors({
  availableRollups,
  filtersVisible,
  toggleFilters,
  account,
  accountId,
  selectedDate,
  riskDate,
  setRiskDate,
  unmodeledDates,
  earliestDate,
  latestDate,
  datesAdjusted,
  filter,
  filterOns,
  modelId,
  normalization,
  portfolio,
  setNormalization,
  filterScope,
  setFilterScope,
  segment,
  selectedScreen,
  selectScreen,
  addNotification,
  setPeriod,
  firstDate,
  startDate,
  endDate,
  period,
  menuItems,
  isActive,
  hasUnsupportedContributionFilter,
  positionSet,
  base,
  baseSignature,
  currency,
  isAiTermsAccepted,
  fxHedgeEnabled,
  updateFilters,
  addedFilters,
  isPortfolioFiltersLoading,
  isOpPro,
}) {
  const saveContextPreferences = useSaveContextPreferences("navigation.analyze.factors")
  const navigate = useNavigate()
  const { wasRedirectedFromPreferences, deletePreferenceAndRedirectToDefault } = useCheckLastPreferenceRedirect()

  useLayoutEffect(() => {
    if (isOpPro && wasRedirectedFromPreferences && selectedScreen === "risk") {
      deletePreferenceAndRedirectToDefault()
    }
  }, [isOpPro, wasRedirectedFromPreferences, deletePreferenceAndRedirectToDefault, selectedScreen])

  const handleSelectAnalyzeView = useCallback(({ id }) => {
    saveContextPreferences(id)
    navigate(`/${account}/p/${portfolio}/analyze/factors/${id}`)
  }, [account, portfolio, navigate, saveContextPreferences])

  const handleSetPeriod = useCallback(change => {
    if (!change.startDate) {
      setPeriod(change)
      return
    }

    if (change.adjusted.startDate || change.adjusted.endDate) {
      const from = change.adjusted.startDate || change.startDate
      const to = change.adjusted.endDate || change.endDate
      addNotification("info", null, `The selected dates have been adjusted to the nearest available date range: ${from} to ${to}.`)
    }

    setPeriod({
      startDate: change.adjusted.startDate || change.startDate,
      endDate: change.adjusted.endDate || change.endDate,
    })
  }, [setPeriod, addNotification])

  const customSelectScreen = <Paper square className="screen-menu-container">
    <Box className="screen-menu-container">
      <SearchableMenu items={menuItems} current={selectedScreen} onSelect={handleSelectAnalyzeView} hideSearchBox className="analyze-factors-select-list" />
    </Box>
  </Paper>

  let customDateSelector
  if (selectedScreen === "risk") {
    customDateSelector = <DateSelector
      {...{
        earliestDate,
        latestDate,
        selectedDate: riskDate,
        exclusionMap: unmodeledDates,
        onChange: setRiskDate,
        adjusted: datesAdjusted,
      }}
    />
  } else {
    customDateSelector = <PeriodSelector
      {...{
        periodAdjusted: datesAdjusted,
        earliestDate,
        latestDate,
        startDate: firstDate || startDate,
        endDate,
        period,
        exclusionMap: unmodeledDates,
        onChange: handleSetPeriod,
        adjusted: datesAdjusted,
      }}
    />
  }

  const perfStartDate = firstDate || startDate
  const customLeftButtons = []

  if (isAiTermsAccepted) {
    customLeftButtons.push(
      <>
        <Divider orientation="vertical" flexItem />
        <AISummarizer {...{
        action: ACTIONS.ANALYZE_FACTORS_SUMMARY,
        account,
        modelId,
        resourceId: portfolio,
        resourceType: "PORTFOLIO",
        ...(isActive && { base }),
        startDate: perfStartDate,
        endDate,
        tooltip: "Open Factor Summary",
      }}
        />
      </>,
    )
  }

  const header = <Header {...{
    updateFilters,
    addedFilters,
    availableRollups,
    filtersVisible,
    toggleFilters,
    account,
    selectedDate,
    filter,
    filterOns,
    modelId,
    normalization,
    assetId: portfolio,
    assetType: "PORTFOLIO",
    setNormalization,
    showFilterScope: true,
    filterScope,
    setFilterScope,
    baseType: base?.type,
    selectedScreen,
    selectScreen,
    customSelectScreen,
    customDateSelector,
    isActive,
    hasUnsupportedContributionFilter,
    currency,
    customLeftButtons,
    isPortfolioFiltersLoading,
  }}
  />

  let main
  if (selectedScreen === "risk") {
    main = <Paper className="details">
      <AnalyzeFactorsRisk {...{
        account,
        accountId,
        modelId,
        selectedDate: riskDate,
        segment,
        positionSet,
        base,
        baseSignature,
        isActive,
        filtersVisible,
        currency,
        fxHedgeEnabled,
      }}
      />
    </Paper>
  } else if (selectedScreen === "performance") {
    main = <AnalyzeFactorsPerformance {...{
      account,
      accountId,
      modelId,
      startDate: perfStartDate,
      endDate,
      segment,
      positionSet,
      base,
      baseSignature,
      isActive,
      filtersVisible,
      currency,
      fxHedgeEnabled,
      displayMetricSelector: true,
    }}
    />
  }
  return <div className="modules_analyze-factors">
    {header}
    {main}
  </div>
}

AnalyzeFactors.propTypes = {
  dispatch: PropTypes.func.isRequired,
  idSort: PropTypes.string,
  availableRollups: PropTypes.array.isRequired,
  filtersVisible: PropTypes.bool.isRequired,
  toggleFilters: PropTypes.func.isRequired,
  account: PropTypes.string.isRequired,
  accountId: PropTypes.string.isRequired,
  selectedDate: DATE,
  riskDate: DATE,
  setRiskDate: PropTypes.func.isRequired,
  unmodeledDates: DATE,
  earliestDate: DATE,
  latestDate: DATE,
  datesAdjusted: PropTypes.bool.isRequired,
  filter: PropTypes.object,
  filterOns: PropTypes.arrayOf(FILTER_ON_GROUP).isRequired,
  modelId: PropTypes.string.isRequired,
  portfolio: PropTypes.string.isRequired,
  portfolioId: PropTypes.string.isRequired,
  normalization: PropTypes.oneOf(map(NORMALIZATION)),
  setNormalization: PropTypes.func.isRequired,
  filterScope: PropTypes.string,
  setFilterScope: PropTypes.func.isRequired,
  segment: PropTypes.object,
  selectedScreen: PropTypes.string.isRequired,
  selectScreen: PropTypes.func.isRequired,
  menuItems: PropTypes.arrayOf(SEARCHABLE_MENU_ITEM).isRequired,
  addNotification: PropTypes.func.isRequired,
  setPeriod: PropTypes.func.isRequired,
  firstDate: DATE,
  startDate: DATE,
  endDate: DATE,
  period: PERIOD,
  isActive: PropTypes.bool.isRequired,
  hasUnsupportedContributionFilter: PropTypes.bool.isRequired,
  positionSet: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }),
  base: PropTypes.object,
  baseSignature: PropTypes.string,
  currency: PropTypes.string,
  isAiTermsAccepted: PropTypes.bool.isRequired,
  fxHedgeEnabled: PropTypes.bool,
  updateFilters: PropTypes.func.isRequired,
  addedFilters: PropTypes.arrayOf(FILTER),
  isPortfolioFiltersLoading: PropTypes.bool.isRequired,
  isOpPro: PropTypes.bool,
}
